import { PieceType } from '../../pieces/types';

import { ApplicationState } from '../../../types';

import { getPieceValue, executePiece } from '../index';

import { ExecutePieceReturnType, getNextPieceIdForComputedField } from './index';
import { DefaultFlowchartProcessState } from '../../types';
import { VariableValueType } from '../../../../helpers/common-types';
import { CustomField, FieldType, getValidCustomFieldValueForInput } from '../../../custom-fields/types';

export function getRoleCustomFieldPieceValue(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, roleId: string, customField: CustomField, pieceId: string): VariableValueType {
    const piecesData = applicationState.flowchart.pieces;
    const piece = piecesData.byId[pieceId];
    const rolesData = applicationState.structure.roles;

    const functionShortHand = getRoleCustomFieldPieceValue.bind({}, applicationState, processState, roleId, customField);

    switch (piece.type) {
        case PieceType.CUSTOM_FIELD:
            if (!piece.customField) {
                throw new Error('A custom field needs to be selected');
            }

            if (piece.customFieldOption) {
                return rolesData.customFieldOptions.byId[piece.customFieldOption].name;
            }

            const customField = rolesData.customFields.byId[piece.customField];

            if (customField.isComputed) {
                let startPiece: string | undefined;

                if (customField.startPiece) {
                    startPiece = customField.startPiece.piece;
                }

                if (typeof startPiece === 'undefined') {
                    return undefined;
                }

                const computedValue = getRoleCustomFieldPieceValue(applicationState, processState, roleId, customField, startPiece);

                return computedValue;
            }

            let customFieldValue = processState.customFields[customField.id];

            if (customField.type === FieldType.SINGLE_SELECT) {
                if (!!customFieldValue) {
                    if (typeof customFieldValue !== 'string') {
                        throw new Error('A single select must have a custom field type');
                    }

                    customFieldValue = rolesData.customFieldOptions.byId[customFieldValue].name;

                }
            }

            if (customField.type === FieldType.MULTI_SELECT) {
                if (!!customFieldValue) {
                    if (!Array.isArray(customFieldValue)) {
                        throw new Error('A multi select must have an array');
                    }

                    customFieldValue = customFieldValue.map(customFieldOptionId => rolesData.customFieldOptions.byId[customFieldOptionId].name);

                }
            }

            return customFieldValue;
        default:
            return getPieceValue(applicationState, processState, pieceId, functionShortHand);
    }
}

// If it returns false, stop execution and display what needs to be displayed. Otherwise, feel free to get the next piece and continue executing
function executePieceForRoleComputedField(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, pieceId: string, roleId: string, customField: CustomField): ExecutePieceReturnType {
    const piecesData = applicationState.flowchart.pieces;
    const piece = piecesData.byId[pieceId];

    const getPieceShorthand = getRoleCustomFieldPieceValue.bind({}, applicationState, processState, roleId, customField);

    switch (piece.type) {

        case PieceType.RETURN:

            if (typeof piece.returnValue === 'undefined') {
                throw new Error('The return piece must have a variable')
            }

            const returnVariablePiece = piecesData.byId[piece.returnValue];
            const returnVariableValue = getPieceShorthand(returnVariablePiece.id);

            return {
                canContinueExecution: false,
                returnValue: returnVariableValue,
            };

        default:
            const canContinueExecution = executePiece(applicationState, processState, pieceId, getPieceShorthand);

            return {
                canContinueExecution,
                returnValue: undefined,
            };
    }
}

export function getRoleComputedFieldValue(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, startPiece: string | undefined, roleId: string, customField: CustomField) {
    let executionResult: ExecutePieceReturnType;
    const getPieceShorthand = getRoleCustomFieldPieceValue.bind({}, applicationState, processState, roleId, customField);

    do {
        const nextPieceId = getNextPieceIdForComputedField(applicationState, processState, startPiece, getPieceShorthand);

        if (typeof nextPieceId === 'undefined') {
            throw new Error('The next piece does not exist');
        }

        executionResult = executePieceForRoleComputedField(applicationState, processState, nextPieceId, roleId, customField);
    } while (executionResult.canContinueExecution);

    const returnValue = executionResult.returnValue;
    return getValidCustomFieldValueForInput(customField, returnValue, applicationState.structure.roles.customFieldOptions);
}